/**
 * Hero gallery — photos appear at the pointer and fade out a few seconds later.
 *
 * <section data-hero-gallery
 *          data-hero-gallery-lifetime="3000"     ← ms a photo stays
 *          data-hero-gallery-step="0.5"          ← trail spacing, share of a photo's long side
 *          data-hero-gallery-team-every="2-4">   ← project photos between two team photos
 *   <div data-hero-gallery-layer></div>        ← photos are appended here
 *   <template data-hero-gallery-images>         ← one <img> per project photo
 *     <img src srcset sizes width height alt="">
 *   </template>
 *   <template data-hero-gallery-team>…</template>  ← optional, team portraits
 *   …text…
 * </section>
 *
 * A mouse leaves a trail: moving over the hero drops the next photo each time
 * the pointer is `step` of a photo away from the previous drop; its clicks
 * drop nothing. A tap (touch, pen) drops a photo where it lands. Every photo
 * fades out `lifetime` ms after it has appeared, earlier once more than
 * MAX_VISIBLE are on screen (oldest first). The editor sets both on the block.
 *
 * Project photos and team portraits are two shuffled decks: every photo of a
 * deck is shown once before the deck is reshuffled (never with the photo just
 * shown on top), and after a random run of team-every project photos one
 * portrait follows — there are far more portraits than projects, so a single
 * mixed pool would be mostly faces. The photos pile up with the newest on top.
 * Positions are stored as percentages of the layer, so the pile keeps its shape
 * when the viewport resizes; the photo's aspect ratio goes to CSS as --ratio,
 * which sizes it (see .hero-gallery-image). Links, buttons and form controls
 * are left alone, so are multi-clicks, the click that ends a text selection and
 * a mouse moved with a button held (a selection in progress). The sources sit
 * in an inert <template>, so they cost nothing at load; the photo due next is
 * fetched ahead — one at a time, not under Save-Data. The trail waits for it,
 * so a photo never turns up where the pointer was a moment ago; a tapped photo
 * that still has to load is revealed once it has arrived.
 *
 * While the hero travels through the viewport every photo lags behind it by
 * its own share of that travel (--parallax), so the pile separates into depth
 * planes on scroll instead of moving as one flat layer.
 *
 * A mouse over the hero becomes a black dot (.hero-cursor, appended to the
 * section) that drags the trail behind it. Touch, pen, links and form controls
 * keep the system cursor.
 */
const MAX_VISIBLE = 10
// Fallbacks for the block's settings: how long a photo stays once it has
// appeared (ms), the distance between two trail photos (share of their long
// side) and the run of project photos before a team portrait
const LIFETIME = 3000
const TRAIL_STEP = 0.5
const TEAM_EVERY = [2, 4]
const IGNORED = 'a, button, input, textarea, select, label, summary'
const onControl = (target) => target instanceof Element && target.closest(IGNORED) !== null

const positive = (value, fallback) => {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? number : fallback
}

const randomInt = (min, max) => min + Math.floor(Math.random() * (max - min + 1))

// Draws the photos of one list in random order, each once per round. A new
// round never starts with the photo that ended the last one.
const deck = (items) => {
  let pile = []
  let last = null
  return () => {
    if (!pile.length) {
      pile = [...items]
      for (let i = pile.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[pile[i], pile[j]] = [pile[j], pile[i]]
      }
      if (pile.length > 1 && pile[pile.length - 1] === last) pile.unshift(pile.pop())
    }
    last = pile.pop()
    return last
  }
}

// The order photos are dropped in: a random run of `every` project photos,
// then one team portrait. With one list empty the other plays alone.
const sequence = (projects, team, [min, max]) => {
  const project = deck(projects)
  const portrait = deck(team)
  if (!team.length) return project
  if (!projects.length) return portrait
  let run = randomInt(min, max)
  return () => {
    if (run > 0) {
      run -= 1
      return project()
    }
    run = randomInt(min, max)
    return portrait()
  }
}

// Parallax depth, cycled per drop: as many factors as the pile holds photos,
// so no two photos on screen ever travel at the same speed. A photo lags
// behind the section by `factor` of the distance the section travels — the
// larger the factor, the further away it reads.
const DEPTHS = [0.12, 0.04, 0.16, 0.08, 0.18, 0.06, 0.14, 0.05, 0.17, 0.1]

document.querySelectorAll('[data-hero-gallery]').forEach((hero) => {
  const layer = hero.querySelector('[data-hero-gallery-layer]')
  const photos = (selector) => [
    ...(hero.querySelector(selector)?.content.querySelectorAll('img') ?? []),
  ]
  const projects = photos('[data-hero-gallery-images]')
  const team = photos('[data-hero-gallery-team]')
  if (!layer || (!projects.length && !team.length)) return

  const lifetime = positive(hero.dataset.heroGalleryLifetime, LIFETIME)
  const trailStep = positive(hero.dataset.heroGalleryStep, TRAIL_STEP)
  const [everyMin, everyMax] = TEAM_EVERY.map((fallback, index) =>
    Math.round(positive(hero.dataset.heroGalleryTeamEvery?.split('-')[index], fallback))
  )
  const nextSource = sequence(projects, team, [everyMin, Math.max(everyMin, everyMax)])

  const shown = []
  // Drawn one drop ahead, so it can be fetched before it is due
  let next = nextSource()
  let drops = 0

  // Fetches the photo due next with the same srcset/sizes the clone will carry,
  // so the browser resolves the same candidate and the drop hits the cache
  // (sizes has to be set before srcset). `upcoming` keeps that copy: the trail
  // drops only once it is complete — loaded, or broken, which the drop then
  // discards. Nothing warmed (Save-Data, or a drop before the page has loaded)
  // counts as due, and that photo is revealed once it arrives.
  const saveData = navigator.connection?.saveData === true
  let upcoming = null
  const warmNext = () => {
    if (saveData) return
    const source = next
    const image = new Image()
    const sizes = source.getAttribute('sizes')
    const srcset = source.getAttribute('srcset')
    if (sizes) image.sizes = sizes
    if (srcset) image.srcset = srcset
    image.src = source.getAttribute('src') ?? ''
    upcoming = image
  }
  const isDue = () => upcoming?.complete ?? true

  // Takes a photo off the pile; false when it was gone already (retired by the
  // cap before its lifetime ran out, or failed to load)
  const forget = (item) => {
    const index = shown.indexOf(item)
    if (index !== -1) shown.splice(index, 1)
    return index !== -1
  }

  const retire = (item) => {
    if (!forget(item)) return
    item.classList.add('is-leaving')
    item.addEventListener('transitionend', () => item.remove(), { once: true })
    // No transition under prefers-reduced-motion (and none when the item was
    // still loading), so remove on a timer as well
    window.setTimeout(() => item.remove(), 600)
  }

  // Depth of every photo on screen. The lag is measured against the section's
  // *visual* travel (its rect, not window.scrollY), so a photo lands exactly
  // under the pointer however far the page is scrolled — and a hero pinned in
  // the stack (body.stack-all) does not travel, so nothing drifts while it is
  // frozen. A retired photo leaves the DOM and with it the loop, so it keeps
  // its offset while it fades out.
  const depths = new WeakMap()
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
  let listening = false
  let frame = 0

  const parallax = () => {
    const rect = layer.getBoundingClientRect()
    // Out of sight: the offsets go stale, and the scroll frame that brings the
    // hero back writes them again before it is painted
    if (rect.bottom <= 0 || rect.top >= window.innerHeight) return
    const travel = -rect.top
    for (const item of layer.children) {
      const depth = depths.get(item)
      if (!depth) continue
      const offset = reducedMotion.matches ? 0 : (travel - depth.from) * depth.factor
      item.style.setProperty('--parallax', `${offset.toFixed(2)}px`)
    }
  }

  const onScroll = () => {
    if (frame) return
    frame = window.requestAnimationFrame(() => {
      frame = 0
      parallax()
    })
  }

  const drop = (clientX, clientY) => {
    const rect = layer.getBoundingClientRect()
    if (!rect.width || !rect.height) return

    const source = next
    next = nextSource()
    warmNext()

    const item = document.createElement('div')
    item.className = 'hero-gallery-item is-loading'
    item.style.left = `${(((clientX - rect.left) / rect.width) * 100).toFixed(3)}%`
    item.style.top = `${(((clientY - rect.top) / rect.height) * 100).toFixed(3)}%`
    const width = Number(source.getAttribute('width'))
    const height = Number(source.getAttribute('height'))
    if (width > 0 && height > 0) item.style.setProperty('--ratio', (width / height).toFixed(4))
    depths.set(item, { factor: DEPTHS[drops % DEPTHS.length], from: -rect.top })
    drops += 1

    const image = source.cloneNode(true)
    image.removeAttribute('fetchpriority')
    image.removeAttribute('loading')
    item.append(image)
    layer.append(item)
    shown.push(item)
    while (shown.length > MAX_VISIBLE) retire(shown[0])

    // Nothing travels before the first drop, so the page pays for the scroll
    // listener only once a photo is on screen
    if (!listening) {
      listening = true
      window.addEventListener('scroll', onScroll, { passive: true })
      window.addEventListener('resize', onScroll, { passive: true })
    }

    // Start the entrance — and the lifetime — only once the photo has arrived
    // (a cached one is complete right away); a photo that fails to load leaves
    // no empty frame
    const reveal = () => {
      item.classList.remove('is-loading')
      window.setTimeout(() => retire(item), lifetime)
    }
    const discard = () => {
      forget(item)
      item.remove()
    }
    if (image.complete && image.naturalWidth > 0) reveal()
    else {
      image.addEventListener('load', reveal, { once: true })
      image.addEventListener('error', discard, { once: true })
    }
  }

  // The trail measures the distance to the previous drop in layer coordinates,
  // where the photos live, so scrolling under a still pointer counts as travel
  // too. The step is a share of the photos' long side, read off a photo on
  // screen (--photo-hero is fluid); with none on screen the last reading
  // stands, so a resting mouse that jitters by a pixel drops nothing.
  let last = null
  let longSide = 0

  const trail = (event) => {
    if (event.pointerType !== 'mouse' || event.buttons !== 0 || onControl(event.target)) return
    if (!isDue()) return
    const rect = layer.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    if (last) {
      const photo = layer.querySelector('img')
      if (photo) longSide = Math.max(photo.offsetWidth, photo.offsetHeight)
      if (Math.hypot(x - last.x, y - last.y) < longSide * trailStep) return
    }
    last = { x, y }
    drop(event.clientX, event.clientY)
  }

  // The dot is fixed to the viewport and moved by pointer events only, so it
  // stays under a still pointer while the page scrolls; once the hero has
  // scrolled away, the section covering it covers the dot as well.
  const cursor = document.createElement('div')
  cursor.className = 'hero-cursor'
  hero.append(cursor)

  // pointerdown as well: a click without a move first (the page loaded under
  // the pointer) still shows the dot, and a tap hides it again
  const track = (event) => {
    const active = event.pointerType === 'mouse' && !onControl(event.target)
    hero.classList.toggle('is-cursor-active', active)
    if (active) cursor.style.translate = `${event.clientX}px ${event.clientY}px`
  }

  // A mouse drops photos by moving, so its clicks drop nothing. Not every
  // browser tells a click's pointer type; the pointerdown before it does.
  let pressedWith = ''

  hero.addEventListener('pointermove', (event) => {
    trail(event)
    track(event)
  })
  hero.addEventListener('pointerdown', (event) => {
    pressedWith = event.pointerType
    track(event)
  })
  hero.addEventListener('pointerleave', () => hero.classList.remove('is-cursor-active'))

  hero.addEventListener('click', (event) => {
    if (pressedWith === 'mouse' || onControl(event.target)) return
    // Double/triple clicks and the click that ends a drag selection are text
    // gestures, not requests for a photo
    if (event.detail > 1) return
    if (document.getSelection()?.isCollapsed === false) return
    drop(event.clientX, event.clientY)
  })

  // The photo due next is fetched once the page has loaded — unless a drop beat
  // the load and fetched it already; every later one when its predecessor is
  // dropped.
  const warmFirst = () => {
    if (!upcoming) warmNext()
  }
  if (document.readyState === 'complete') warmFirst()
  else window.addEventListener('load', warmFirst, { once: true })
})
